import { useState } from 'react';
import { FiPackage } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import { formatBDT } from '@maktech/shared';
import { errorMessage } from '@/lib/api/client';
import { createBookOrder } from '@/api/commerce.api';
import { useBooks } from '@/hooks/api/useCommerceQueries';
import { Modal } from '@/components/common/Modal';
import { StateIcon } from '@/components/common/StateIcon';
import { useAuth } from '@/features/auth/AuthContext';
import { useCart } from '@/features/commerce/CartContext';

/**
 * Book delivery + cash on delivery — placeBookOrder() (:5071).
 *
 * PROTOTYPE BUG NOT REPRODUCED: placeBookOrder ran `arr.length=0` and THEN
 * built its confirmation with bookTotal()+60 — reading an already-emptied cart,
 * so every confirmation told the customer "Cash on delivery: ৳60" no matter
 * what they ordered. The order row was right; only the message the human read
 * was wrong. Here the amount comes from the server's response, so the number
 * confirmed is the number recorded.
 */
export function ShipModal({ onClose }: { onClose: () => void }) {
  const { books, clearBooks } = useCart();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState<{ orderId: string; total: number } | null>(null);

  const { data } = useBooks();

  const subtotal = books.reduce((s, l) => {
    const b = data?.books.find((x) => x.slug === l.slug);
    return s + (b ? b.price * l.qty : 0);
  }, 0);
  const total = subtotal + 6000;

  async function place() {
    setBusy(true);
    setError(null);
    try {
      const r = await createBookOrder({ items: books, name, phone, address });
      clearBooks();
      await qc.invalidateQueries({ queryKey: ['books'] }); // stock moved
      setPlaced({ orderId: r.orderId, total: r.total });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (placed) {
    return (
      <Modal onClose={onClose} label="Order confirmed">
        <div className="px-2.5 py-6 text-center text-[var(--ink-soft)]" data-testid="ship-done">
          <StateIcon>
            <FiPackage className="size-8" aria-hidden />
          </StateIcon>
          <b>Order placed — {placed.orderId}</b>
          <p>
            Your books are being packed. Delivery takes 2–4 working days inside Dhaka, 4–7 outside.
            <br />
            {/* The REAL amount, from the server. */}
            Cash on delivery: <b data-testid="ship-cod-total">{formatBDT(placed.total)}</b>.
          </p>
          <button className="inline-flex items-center justify-center rounded-full bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] px-4 py-[9px] text-[13.5px] font-semibold text-white shadow-[var(--shadow-orange)]" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} label="Delivery details">
      <h3 className="mb-1">Delivery details</h3>
      <p className="mb-3 text-[var(--ink-soft)]">
        Cash on delivery — pay the courier when your books arrive.
      </p>

      <div className="mb-3.5">
        <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="shName">Full name</label>
        <input className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="shName" data-testid="ship-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="mb-3.5">
        <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="shPhone">Phone</label>
        <input className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="shPhone" data-testid="ship-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
      </div>
      <div className="mb-3.5">
        <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="shAddr">Full address</label>
        <textarea className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="shAddr" data-testid="ship-address" rows={3} value={address} onChange={(e) => setAddress(e.target.value)} required />
      </div>

      <div className="my-2.5 flex justify-between font-extrabold">
        <span>Total (incl. {formatBDT(6000)} delivery)</span>
        <span id="shipTotal" data-testid="ship-total">
          {formatBDT(total)}
        </span>
      </div>

      {error ? (
        <p data-testid="ship-error" className="text-[13.5px] text-[var(--coral)]">
          {error}
        </p>
      ) : null}

      <button className="inline-flex w-full items-center justify-center rounded-full bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] px-6 py-3 text-[15px] font-semibold text-white shadow-[var(--shadow-orange)] disabled:opacity-60" data-testid="ship-place" disabled={busy} onClick={() => void place()}>
        {busy ? 'Placing…' : 'Place order (cash on delivery)'}
      </button>
    </Modal>
  );
}
